import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/shadcn/pagination";

import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface MatchesPaginationProps {
    pageIndex: number;
    totalPages: number;
    setPageIndex: (index: number) => void;
}

const getPageNumbers = (totalPages: number, currentPage: number) => {
    if (totalPages <= 9) {
        return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pageNumbers: ("ellipsis" | number)[] = [0];

    if (currentPage <= 4) {
        pageNumbers.push(...Array.from({ length: 5 }, (_, i) => i + 1), "ellipsis");
    } else if (currentPage >= totalPages - 5) {
        pageNumbers.push("ellipsis", ...Array.from({ length: 5 }, (_, i) => totalPages - 6 + i));
    } else {
        pageNumbers.push("ellipsis", ...Array.from({ length: 3 }, (_, i) => currentPage - 1 + i), "ellipsis");
    }

    pageNumbers.push(totalPages - 1);

    return pageNumbers;
};

export function SelectMatchesDialogPagination({ pageIndex, totalPages, setPageIndex }: MatchesPaginationProps) {
    const pageNumbers = getPageNumbers(totalPages, pageIndex);

    return (
        <div className="w-full">
            <Pagination className="w-full">
                <PaginationContent className="w-full flex justify-between items-center px-4">
                    <PaginationItem>
                        <button
                            onClick={() => setPageIndex(Math.max(0, pageIndex - 1))}
                            disabled={pageIndex === 0}
                            className="px-3 py-1.5 border border-outline-variant rounded-md flex items-center justify-center cursor-pointer disabled:opacity-50 bg-surface-container hover:bg-surface-container-high"
                        >
                            <ArrowLeft className="w-4 h-4 text-on-surface-variant" />
                        </button>
                    </PaginationItem>
                    <div className="flex gap-1">
                        {pageNumbers.map((page, i) => (
                            <PaginationItem key={i}>
                                {page === "ellipsis" ? (
                                    <span className="px-2 text-sm text-on-surface-variant leading-8">...</span>
                                ) : (
                                    <PaginationLink
                                        href="#"
                                        isActive={page === pageIndex}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPageIndex(page);
                                        }}
                                        className={cn(
                                            "h-8 min-w-[32px] px-2 text-sm text-center rounded-md border transition-none focus:outline-none",
                                            page === pageIndex
                                                ? "bg-primary text-white font-medium border-primary"
                                                : "border-transparent text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
                                        )}
                                    >
                                        {page + 1}
                                    </PaginationLink>
                                )}
                            </PaginationItem>
                        ))}
                    </div>
                    <PaginationItem>
                        <button
                            onClick={() => setPageIndex(Math.min(totalPages - 1, pageIndex + 1))}
                            disabled={pageIndex >= totalPages - 1}
                            className="px-3 py-1.5 border border-outline-variant rounded-md flex items-center justify-center cursor-pointer disabled:opacity-50 bg-surface-container hover:bg-surface-container-high"
                        >
                            <ArrowRight className="w-4 h-4 text-on-surface-variant" />
                        </button>
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
}
